import { useLocation } from '@reach/router';
import classnames from 'classnames';
import { navigate } from 'gatsby';
import React, { useEffect, useState } from 'react';
import './TabsHeader.scss';
import useWindowSize from '../../hooks/useWindowSize';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Tab } from '../../entities/Tabs';
import { MDXRenderer } from 'gatsby-plugin-mdx';

interface TabsProps {
  tabs: Array<Tab>;
  activeTab: string;
  slidesPerView?: number;
  setTabIndex?: number;
  longTail?: boolean;
}

export function TabsHeader({ tabs, activeTab, slidesPerView, setTabIndex, longTail }: TabsProps): React.ReactElement {
  const [selectedTab, setSelectedTab] = useState<string>(activeTab);
  const location = useLocation();
  const { width } = useWindowSize();
  useEffect(() => {
    if (location.hash) {
      tabs.forEach((tab, index) => {
        if (tab.url === location.hash.substring(1) || tab.url === decodeURIComponent(location.hash.substring(1))) {
          setSelectedTab(tabs[index].title);
        }
      });
    } else {
      if (setTabIndex) setSelectedTab(tabs[setTabIndex].title);
      else setSelectedTab(tabs[0].title);
    }
    if (selectedTab === '') setSelectedTab(tabs[0].title);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabs, location, setTabIndex]);

  if (tabs.length === 0) return;
  return (
    <div className="tabs">
      {!width || width >= 1000 ? (
        <div className={classnames('tabs__header', { 'tabs__header--longtail': longTail })}>
          {tabs.map((tab) => {
            if (tab.children === null) return null;
            return (
              <div className="tabs__item" key={tab.title}>
                <button
                  id={tab.anchor && tab.url}
                  className={classnames({ 'tabs__item--selected': tab.title === selectedTab })}
                  onClick={(e) => {
                    e.preventDefault();
                    setSelectedTab(tab.title);
                    window.history.pushState(null, '', '#' + tab.url);
                  }}
                >
                  {tab.title.replace(/`/g, '')}
                </button>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="tabs__header">
          <Swiper
            spaceBetween={0}
            initialSlide={0}
            slidesPerView={slidesPerView || 2.5}
            grabCursor={true}
            watchOverflow
            centeredSlides={false}
            direction={'horizontal'}
            loop={false}
            className="tabs__header--swiper"
            freeMode={false}
            touchMoveStopPropagation
            preventClicks
            preventClicksPropagation
          >
            {tabs.map((tab) => {
              if (tab.children === null) return null;
              return (
                <SwiperSlide key={tab.title}>
                  <div
                    className="tabs__item"
                    onClick={() => {
                      navigate('#' + tab.url);
                    }}
                  >
                    <button className={classnames({ 'tabs__item--selected': tab.title === selectedTab })} onClick={() => setSelectedTab(tab.title)}>
                      {tab.title.replace(/`/g, '')}
                    </button>
                  </div>
                </SwiperSlide>
              );
            })}
          </Swiper>
        </div>
      )}
      <div className="tabs__content">
        {tabs
          .filter((tab) => tab.title === selectedTab)
          .map((tab) => {
            return (
              <div key={tab.title} className="tabs__box">
                {tab.description && <p className="tabs__description">{tab.description}</p>}
                {tab.content && (
                  <p className="tabs__description">
                    <MDXRenderer>{tab.content.childMdx.body}</MDXRenderer>
                  </p>
                )}
                {tab.children}
              </div>
            );
          })}
      </div>
    </div>
  );
}
